import { Fragment, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import {
  PUSH_ON_BY_DEFAULT,
  type NotificationCategory,
  type NotificationPreferences,
} from '@allemnii-pulse/shared';
import { useAuth } from '../auth/AuthProvider.js';
import { db } from '../../lib/firebase.js';
import { useDocument } from '../../lib/firestore.js';
import {
  disablePush,
  enablePush,
  isPushEnabledLocally,
  pushPermission,
  sendTestPush,
  type TestPushResult,
} from '../../lib/messaging.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { Button } from '../../components/ui/Button.js';
import { NOTIFICATION_CATEGORY_LABEL } from '../notifications/notificationMeta.js';
import { ConnectedAppsCard } from '../mcp/ConnectedAppsCard.js';
import { IosInstallDialog } from '../pwa/InstallBanner.js';
import { isIOS, isStandalone, promptInstall, useCanPromptInstall } from '../../lib/pwa.js';

/** What a test push's outcome means, in words that say what to do next. */
function describeTestPush(result: TestPushResult): { ok: boolean; text: string } {
  if (result.devices === 0) {
    return {
      ok: false,
      text: 'No device is registered for your account. Disable push, then enable it again.',
    };
  }
  if (result.sent > 0) {
    const others =
      result.devices && result.devices > 1 ? ` (${result.sent} of ${result.devices} devices)` : '';
    return {
      ok: true,
      text: `Sent${others}. It should arrive in a few seconds — if this app is open on this page, it may not pop up.`,
    };
  }
  const errors = result.errors.join(', ');
  if (/permission|auth|credential|PERMISSION_DENIED|forbidden/i.test(errors)) {
    return {
      ok: false,
      text: `The server isn't allowed to send push notifications (${errors}). An admin needs to give the API's service account the Firebase Cloud Messaging role and enable the FCM API.`,
    };
  }
  return { ok: false, text: `The push wasn't delivered (${errors || 'unknown error'}).` };
}

const CATEGORIES = Object.keys(NOTIFICATION_CATEGORY_LABEL) as NotificationCategory[];

/** The always-available way to install, for whoever dismissed the mobile banner (or is on a
 * desktop, which never shows it). Hidden inside the installed app itself. */
function InstallAppCard() {
  const canPrompt = useCanPromptInstall();
  const [showIosSteps, setShowIosSteps] = useState(false);
  if (isStandalone()) return null;
  const ios = isIOS();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Install the app</CardTitle>
        <CardDescription>
          Add Pulse to your home screen or dock: it opens full-screen like any other app
          {ios ? ', and on iPhone it’s required for push notifications.' : '.'}
        </CardDescription>
      </CardHeader>
      {!canPrompt && !ios && (
        <CardContent>
          <p className="text-sm text-muted">
            Use your browser's own Install option — the icon at the end of the address bar, or
            “Install app” / “Add to Home screen” in its menu.
          </p>
        </CardContent>
      )}
      {(canPrompt || ios) && (
        <CardFooter className="justify-end">
          <Button
            className="h-10 sm:h-8"
            onClick={() => (ios ? setShowIosSteps(true) : void promptInstall())}
          >
            {ios ? 'How to install' : 'Install'}
          </Button>
        </CardFooter>
      )}
      <IosInstallDialog open={showIosSteps} onOpenChange={setShowIosSteps} />
    </Card>
  );
}

/** Per-user notification preferences and the browser push opt-in. See docs/PLAN.md §7.3. Prefs
 * write straight to Firestore (users/{uid}/prefs/notifications is one of the few
 * client-writable documents — no audit meaning), same as the theme preference. */
export function SettingsPage() {
  const { user } = useAuth();
  const { data: prefs } = useDocument<NotificationPreferences>(
    user ? `users/${user.uid}/prefs/notifications` : null,
  );

  const [permission, setPermission] = useState(pushPermission());
  const [pushEnabled, setPushEnabled] = useState(isPushEnabledLocally);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function setPref(
    category: NotificationCategory,
    channel: 'inApp' | 'push',
    value: boolean,
  ) {
    if (!user) return;
    await setDoc(
      doc(db, `users/${user.uid}/prefs/notifications`),
      { [category]: { [channel]: value } },
      { merge: true },
    );
  }

  async function handleEnablePush() {
    setPushBusy(true);
    setPushError(null);
    const result = await enablePush();
    if (result.ok) {
      setPermission(pushPermission());
      setPushEnabled(true);
    } else {
      setPermission(pushPermission());
      setPushError(result.reason);
    }
    setPushBusy(false);
  }

  async function handleTestPush() {
    setPushBusy(true);
    setTestResult(null);
    try {
      setTestResult(describeTestPush(await sendTestPush()));
    } catch {
      setTestResult({ ok: false, text: "Couldn't reach the server. Try again." });
    } finally {
      setPushBusy(false);
    }
  }

  async function handleDisablePush() {
    setPushBusy(true);
    await disablePush();
    setPushEnabled(false);
    setPushBusy(false);
  }

  const canEnable = permission !== 'unsupported' && permission !== 'denied';
  const isActive = pushEnabled && permission === 'granted';

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 sm:gap-6 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">Settings</h1>
        <p className="text-sm text-muted">
          Manage how you get notified, and what has access to your account.
        </p>
      </div>

      <InstallAppCard />

      <ConnectedAppsCard />

      <Card>
        <CardHeader>
          <CardTitle>Browser push notifications</CardTitle>
          <CardDescription>
            Get a system notification even when Allemnii Pulse isn't open in a tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {permission === 'unsupported' && (
            <p className="text-sm text-muted">
              {isIOS() && !isStandalone()
                ? 'On iPhone and iPad, install Pulse to your Home Screen first (above), then turn push on from the installed app.'
                : 'Not supported in this browser.'}
            </p>
          )}
          {permission === 'denied' && (
            <p className="text-sm text-danger">
              Notifications are blocked for this site in your browser settings. Allow them there,
              then reload this page.
            </p>
          )}
          {pushError && <p className="text-sm text-danger">{pushError}</p>}
          {isActive && <p className="text-sm text-success">Push is on for this device.</p>}
          {testResult && (
            <p className={`text-sm ${testResult.ok ? 'text-muted' : 'text-danger'}`}>
              {testResult.text}
            </p>
          )}
        </CardContent>
        {canEnable && (
          <CardFooter className="flex-wrap justify-end gap-2">
            {isActive ? (
              <>
                <Button
                  variant="outline"
                  className="h-10 sm:h-8"
                  disabled={pushBusy}
                  onClick={() => void handleDisablePush()}
                >
                  Disable push
                </Button>
                <Button
                  className="h-10 sm:h-8"
                  disabled={pushBusy}
                  onClick={() => void handleTestPush()}
                >
                  {pushBusy ? 'Working…' : 'Send a test notification'}
                </Button>
              </>
            ) : (
              <Button
                className="h-10 sm:h-8"
                disabled={pushBusy}
                onClick={() => void handleEnablePush()}
              >
                {pushBusy ? 'Working…' : 'Enable push'}
              </Button>
            )}
          </CardFooter>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notification preferences</CardTitle>
          <CardDescription>
            Choose what shows up in-app and what sends a push, per category.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-4 gap-y-3">
            <span className="text-xs font-medium uppercase text-muted">Category</span>
            <span className="text-xs font-medium uppercase text-muted">In-app</span>
            <span className="text-xs font-medium uppercase text-muted">Push</span>
            {CATEGORIES.map((category) => {
              const pref = prefs?.[category];
              return (
                <Fragment key={category}>
                  <span className="text-sm">{NOTIFICATION_CATEGORY_LABEL[category]}</span>
                  <Checkbox
                    checked={pref?.inApp ?? true}
                    onCheckedChange={(v) => void setPref(category, 'inApp', v === true)}
                    aria-label={`${NOTIFICATION_CATEGORY_LABEL[category]} — in-app`}
                  />
                  <Checkbox
                    checked={pref?.push ?? PUSH_ON_BY_DEFAULT.includes(category)}
                    disabled={!isActive}
                    onCheckedChange={(v) => void setPref(category, 'push', v === true)}
                    aria-label={`${NOTIFICATION_CATEGORY_LABEL[category]} — push`}
                  />
                </Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
